import React from 'react';
import { motion } from 'framer-motion';
import { Utensils, LayoutTemplate, User, ShoppingBag, Code2, Repeat, ArrowUpRight } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { useInView } from '@/lib/hooks';
import { SERVICES } from '@/constants/testIds';

const CARDS = [
  { icon: Utensils,       title: 'Restaurant & Café Sites',   copy: 'Your menu deserves better than a blurry PDF link.' },
  { icon: LayoutTemplate,  title: 'Business Landing Pages',    copy: 'One page. One goal. Unlimited leads.' },
  { icon: User,            title: 'Portfolio & Personal Brand', copy: 'Look like the expert you already are.' },
  { icon: ShoppingBag,     title: 'E-commerce Stores',         copy: 'Sell while you sleep. Literally.' },
  { icon: Code2,           title: 'Custom Web Apps',           copy: 'If you can imagine it, we can ship it.' },
  { icon: Repeat,          title: 'Website Redesigns',         copy: 'Your old site is costing you clients. Let’s fix that.' },
];

function ServiceCard({ Icon, title, copy, index, inView }) {
  return (
    <motion.div
      data-testid={SERVICES.card(index)}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="card-light group p-7 relative overflow-hidden"
    >
      {/* Decorative hex outline that draws on hover */}
      <svg className="absolute -top-8 -right-8 opacity-30 group-hover:opacity-60 transition-opacity duration-500" width="120" height="120" viewBox="0 0 120 120" aria-hidden>
        <polygon
          points="60,8 110,34 110,86 60,112 10,86 10,34"
          fill="none"
          stroke="#E89A1F"
          strokeWidth="1"
          strokeDasharray="320"
          className="hex-draw-stroke"
        />
      </svg>

      <div
        className="relative inline-flex items-center justify-center w-11 h-11 rounded-lg mb-5"
        style={{ background: '#FFF6E5', border: '1px solid #F5DFAE' }}
      >
        <Icon size={20} className="text-[color:var(--sh-amber-2)]" />
      </div>
      <h3 className="font-display text-[1.30rem] md:text-[1.40rem] font-semibold leading-snug text-[color:var(--sh-ink)]">{title}</h3>
      <p className="mt-2 text-[15px] text-[color:var(--sh-muted)] leading-relaxed">{copy}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-accent text-[color:var(--sh-amber-2)]">
        <span className="transition-transform group-hover:translate-x-0.5">Tell me more</span>
        <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
      <style>{`.group:hover .hex-draw-stroke { animation: hex-stroke-draw 1.2s ease forwards; }
        @keyframes hex-stroke-draw { from { stroke-dashoffset: 320; } to { stroke-dashoffset: 0; } }
      `}</style>
    </motion.div>
  );
}

export default function Services() {
  const [ref, inView] = useInView({ threshold: 0.15, once: true });
  return (
    <section id="services" className="section" data-testid={SERVICES.block}>
      <div className="container-pro">
        <SectionHeading
          eyebrow="WHAT WE DO"
          title="Every business deserves a website that actually works."
          subtitle="We specialize in sites that don’t just look good — they convert visitors into paying customers."
        />
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CARDS.map((c, i) => (
            <ServiceCard key={i} Icon={c.icon} title={c.title} copy={c.copy} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
